/**
 * AK-938 — TanStack Query слой раздела «Наука» (ADR-086).
 *
 *   GET    /trial-schemes, /trial-schemes/{id}     — схемы опытов организации
 *   POST   /trial-schemes, PUT /trial-schemes/{id} — создать / заменить схему целиком
 *   DELETE /trial-schemes/{id}                     — в архив
 *   GET    /trials                                 — «все мои опыты» (реестр + свой кабинет)
 *   GET    /fields/{fid}/trials                    — опыты поля
 *   GET    /fields/{fid}/trials/{tid}              — сводка опыта (варианты, лента, урожайность)
 *
 * Запись опыта («Заложить опыт», «Закрыть») — одним действием из нескольких
 * запросов, поэтому живёт на своих экранах (`pages/science/place.tsx`,
 * `components/science/CloseTrialSheet.tsx`), а не здесь.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  FieldTrial,
  TrialListOut,
  TrialScheme,
  TrialSchemeBody,
  TrialSchemeListItem,
  TrialSummary,
} from './types';

export const scienceKeys = {
  all: ['science'] as const,
  schemes: () => ['science', 'schemes'] as const,
  scheme: (id: string) => ['science', 'scheme', id] as const,
  trials: (params: TrialsQuery) => ['science', 'trials', params] as const,
  fieldTrials: (fieldId: string) => ['science', 'field-trials', fieldId] as const,
  summary: (fieldId: string, trialId: string) =>
    ['science', 'summary', fieldId, trialId] as const,
};

export interface TrialsQuery {
  seasonYear?: number;
  q?: string;
  mine?: boolean;
  perPage?: number;
}

export function trialsQueryString(p: TrialsQuery): string {
  const qs = new URLSearchParams({ page: '1', per_page: String(p.perPage ?? 100) });
  if (p.seasonYear != null) qs.set('season_year', String(p.seasonYear));
  if (p.q?.trim()) qs.set('q', p.q.trim());
  if (p.mine) qs.set('mine', 'true');
  return qs.toString();
}

export function useTrialSchemes(): UseQueryResult<TrialSchemeListItem[], Error> {
  return useQuery({
    queryKey: scienceKeys.schemes(),
    queryFn: () => api.get<TrialSchemeListItem[]>('/api/v1/trial-schemes'),
    staleTime: 30_000,
  });
}

export function useTrialScheme(id: string | null): UseQueryResult<TrialScheme, Error> {
  return useQuery({
    queryKey: scienceKeys.scheme(id ?? ''),
    queryFn: () => api.get<TrialScheme>(`/api/v1/trial-schemes/${id}`),
    enabled: !!id,
  });
}

export function useTrials(params: TrialsQuery): UseQueryResult<TrialListOut, Error> {
  return useQuery({
    queryKey: scienceKeys.trials(params),
    queryFn: () => api.get<TrialListOut>(`/api/v1/trials?${trialsQueryString(params)}`),
    staleTime: 30_000,
  });
}

export function useFieldTrials(fieldId: string): UseQueryResult<FieldTrial[], Error> {
  return useQuery({
    queryKey: scienceKeys.fieldTrials(fieldId),
    queryFn: () => api.get<FieldTrial[]>(`/api/v1/fields/${fieldId}/trials`),
    enabled: !!fieldId,
    staleTime: 30_000,
  });
}

export function useTrialSummary(
  fieldId: string,
  trialId: string,
): UseQueryResult<TrialSummary, Error> {
  return useQuery({
    queryKey: scienceKeys.summary(fieldId, trialId),
    queryFn: () =>
      api.get<TrialSummary>(
        `/api/v1/fields/${fieldId}/trials/${trialId}?include_geojson=true`,
      ),
    enabled: !!fieldId && !!trialId,
  });
}

/** Создать (id = null) или заменить схему целиком. */
export function useSaveTrialScheme(id: string | null) {
  const qc = useQueryClient();
  return useMutation<TrialScheme, Error, TrialSchemeBody>({
    mutationFn: (body) =>
      id
        ? api.put<TrialScheme>(`/api/v1/trial-schemes/${id}`, body)
        : api.post<TrialScheme>('/api/v1/trial-schemes', body),
    onSettled: () => qc.invalidateQueries({ queryKey: scienceKeys.all }),
  });
}

export function useArchiveTrialScheme() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => api.delete<void>(`/api/v1/trial-schemes/${id}`),
    onSettled: () => qc.invalidateQueries({ queryKey: scienceKeys.all }),
  });
}
